import React from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  PillTabs,
  PillTabsList,
  PillTabsTriggerHint,
  PillTabsTrigger,
  PillTabsContent,
} from "~/react/ui/pill-tabs"
import { TooltipProvider } from "~/react/ui/tooltip"
import {
  Filters,
  clearFilters,
  departmentsList,
  filterFunction,
  sortFunction,
  type FiltersType,
} from "~/react/components/search/filters"
import { CostCalculator } from "~/helpers/costCalculator"
import {
  Result,
  ResultType,
} from "~/react/components/multi_domain_search/result"
import { type TabSelected } from "~/react/types/tabSelected"
import {
  leads_path,
  multi_domain_search_emails_search_index_path,
  multi_domain_search_reveal_search_index_path,
  mappings_locations_path,
} from "~/lib/routes"
import * as ActionCable from "@rails/actioncable"
import { useAsyncQueuer, batch } from "@tanstack/react-pacer"
import BulkActionsToolbar from "~/react/components/search/bulk-actions-toolbar"
import { Alert, AlertIcon, AlertContent } from "../ui/alert"
import { DepartmentSection } from "~/react/components/search/department_section"
import { EmptyState } from "~/react/components/search/empty-state"
import {
  revealRequest as sharedRevealRequest,
  createProcessResult,
} from "~/react/utils/emailSearchUtils"
import { useCampaignsSave } from "~/react/hooks/useCampaignsSave"
import { useLeadsListSave } from "~/react/hooks/useLeadsListSave"
import { EmailVerification } from "~/types/serializers/EmailVerification"
import { useJobTitleSuggestions } from "~/react/hooks/useJobTitleSuggestions"
import { useSplittedResults } from "../hooks/useSplittedResults"
import EmailsList from "./search/emails-list"
import { useLocationMappings } from "../hooks/useLocationMappings"
import { Snackbar, SnackbarText, SnackbarLink } from "../ui/snackbar"

const estimatedSize = 80

interface DomainSearchProps {
  emailIds: number[]
  numberOfResults: number
  genericEmailsCount: number
  personalEmailsCount: number
  decisionMakerTotalCount: number
  departmentTotalCounts: Record<string, number>
  filterParams: Record<string, any>
  domain: {
    value: string
    company_name?: string
    company_data: boolean
  }
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  numberOfCreditsLeft: number
  singleCreditsBucket: boolean
  jobTitleProposals: string[]
  revealEmailPattern: boolean
  linkedDomains: string[]
  companySignal?: any
  clickToCopy?: boolean
}

type LocationsMappingsType = {
  continents: string[]
  business_regions: string[]
}

const revealRequest = sharedRevealRequest

export default function DomainSearch({
  emailIds: initialEmailIds,
  numberOfResults,
  genericEmailsCount,
  personalEmailsCount,
  decisionMakerTotalCount,
  departmentTotalCounts,
  filterParams,
  domain,
  numberOfCreditsLeft,
  singleCreditsBucket,
  leadsLists: initialLeadsLists,
  campaigns: initialCampaigns,
  jobTitleProposals,
  revealEmailPattern,
  linkedDomains,
  companySignal,
  clickToCopy = false,
}: DomainSearchProps) {
  const [revealedIds, setRevealedIds] = React.useState<number[]>([])
  const [firstRevealAt, setFirstRevealAt] = React.useState<Date | null>(null)
  const [results, setResults] = React.useState<ResultType[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [reavealingIds, setRevealingIds] = React.useState<number[]>([])
  const [leadsLists, setLeadsLists] =
    React.useState<{ id: number; name: string }[]>(initialLeadsLists)
  const [campaigns, setCampaigns] =
    React.useState<{ id: number; name: string }[]>(initialCampaigns)
  const [tabSelected, setTabSelected] =
    React.useState<TabSelected>("all_people")
  const locationsMappings = useLocationMappings()
  const [filters, setFilters] = React.useState<FiltersType>({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: false,
    location: [],
  })
  const [selectedIds, setSelectedIds] = React.useState([])

  // Loading hook for fetching emails
  const fetchEmailsFn = async (emailIds: number[]) => {
    const response = await fetch(
      multi_domain_search_emails_search_index_path(),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            ?.content,
        },
        body: JSON.stringify({ email_ids: emailIds }),
      },
    )
    const data = await response.json()

    setResults((prev) => {
      const existingIds = new Set(prev.map((result) => result.id))
      const newResults = data.emails.filter(
        (email) => !existingIds.has(email.id),
      )
      return [...prev, ...newResults]
    })
    if (queue.peekPendingItems().length === 0) {
      setIsLoading(false)
    }
  }
  const queue = useAsyncQueuer(fetchEmailsFn, { concurrency: 10, wait: 10 })

  React.useEffect(() => {
    setRevealingIds((prev) => prev.filter((id) => !revealedIds.includes(id)))
  }, [revealedIds])

  // Load initial emails
  React.useEffect(() => {
    if (initialEmailIds.length > 0) {
      setIsLoading(true)
      const batchSize = 5000

      for (let i = 0; i < initialEmailIds.length; i += batchSize) {
        queue.addItem(initialEmailIds.slice(i, i + batchSize))
      }
    }
  }, [initialEmailIds])

  const processResult = batch<ResultType>(
    createProcessResult(
      setResults,
      setRevealedIds,
      setRevealingIds,
      setFirstRevealAt,
    ),
    {
      maxSize: 10,
      wait: 250,
    },
  )

  // WebSocket connection for real-time updates
  React.useEffect(() => {
    const consumer = ActionCable.createConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: "MultiDomainSearchChannel" },
      {
        received: (
          data:
            | { email: ResultType }
            | {
                verification: EmailVerification
              },
        ) => {
          if ("email" in data) {
            processResult(data.email)
          } else if ("verification" in data) {
            setResults((prev) =>
              prev.map((result) =>
                result.id === data.verification.email_id
                  ? { ...result, verification: data.verification }
                  : result,
              ),
            )
          }
        },
      },
    )

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const handleReveal = async (id: number | "generic") => {
    const result = results.find((result) => result.id === id)

    if (id === "generic" || (result && result.type === "generic")) {
      const result = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        {
          generic_email_domains: [domain.value],
          source: "search",
        },
      )

      if (result === "lack_of_credits") {
        setRevealingIds([])
      } else if (id === "generic") {
        setRevealingGeneric(true)
      } else {
        setRevealingIds((prev) => [...prev, id])
      }
    } else {
      const result = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        {
          email_ids: [id],
          source: "search",
        },
      )

      if (result === "lack_of_credits") {
        setRevealingIds([])
      } else {
        setRevealingIds((prev) => [...prev, id])
      }
    }
  }

  const handleCampaignsSave = useCampaignsSave(results, campaigns, setCampaigns)

  const handleLeadsListSave = useLeadsListSave(
    results,
    setLeadsLists,
    setResults,
  )

  // Filter and sort results
  const filteredResults = React.useMemo(() => {
    const baseFilteredResults = filterFunction(
      results,
      filters,
      locationsMappings,
      revealedIds,
    )

    return baseFilteredResults.sort(sortFunction)
  }, [results, filters, revealedIds])

  const { allPeopleResults, decisionMakersResults, genericResults } =
    useSplittedResults(filteredResults)

  const handleSelect = (ids: number[]) => {
    let nextIds = [...selectedIds]
    ids.forEach((id) => {
      if (nextIds.includes(id)) {
        nextIds = nextIds.filter((item) => item !== id)
      } else {
        nextIds.push(id)
      }
    })
    setSelectedIds(nextIds)
  }

  const jobTitles = useJobTitleSuggestions(
    results,
    filters,
    locationsMappings,
    revealedIds,
  )

  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.hideAlreadySaved ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0

  // Bulk actions functions
  const saveLeads = async (listId: number | null) => {
    if (selectedIds.length > 0) {
      // Reveal only specific emails
      const selectedEmails = filteredResults.filter((result) =>
        selectedIds.includes(result.id),
      )
      const notGenericEmails = selectedEmails.filter(
        (result) => result.type !== "generic",
      )
      setRevealingIds((prev) => [
        ...prev,
        ...notGenericEmails
          .filter((result) => !result.revealed)
          .map((result) => result.id),
        ...selectedEmails.map((result) => result.id),
      ])
      const genericEmailsDomains = new Set(
        selectedEmails
          .filter((result) => result.type === "generic")
          .map((result) => result.domain),
      )

      if (genericEmailsDomains.size > 0) {
        // Reveal generic emails
        const result = await revealRequest(
          multi_domain_search_reveal_search_index_path(),
          "POST",
          {
            generic_email_domains: Array.from(genericEmailsDomains),
            leads_list_id: listId,
            source: "search",
          },
        )

        if (result === "lack_of_credits") {
          setRevealingIds([])
        } else {
          setRevealingGeneric(true)
        }
      }
      if (notGenericEmails.length > 0) {
        // Reveal specific emails
        const result = await revealRequest(
          multi_domain_search_reveal_search_index_path(),
          "POST",
          {
            email_ids: notGenericEmails.map((result) => result.id),
            leads_list_id: listId,
            source: "search",
          },
        )

        if (result === "lack_of_credits") {
          setRevealingIds([])
        } else {
          setRevealingIds((prev) => [
            ...prev,
            ...notGenericEmails.map((result) => result.id),
          ])
        }
      }
    }
  }

  const exportEmails = () => {
    const formData = new FormData()
    formData.append("email_ids", selectedIds.join(","))

    fetch(`/search/${domain.value}/download`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
    })
      .then((response) => {
        const contentType = response.headers.get("content-type")

        if (contentType && contentType.includes("application/json")) {
          // Handle JSON response
          return response.json().then((data) => {
            if (data.message) {
              App.displayFlash("success", data.message)
            }
          })
        } else if (contentType && contentType.includes("text/csv")) {
          // Handle CSV file download
          return response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = "export.csv"
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
          })
        }
      })
      .catch(() => {
        App.displayFlash(
          "danger",
          i18n.t("js.front.api_errors.internal_server_error"),
        )
      })
  }

  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    saveLeads(listId)
  }

  // Calculate costs
  let numberOfEmailsToReveal = 0
  if (selectedIds.length > 0) {
    numberOfEmailsToReveal = results.filter(
      (result) => selectedIds.includes(result.id) && !result.revealed,
    ).length
  }

  const numberOfCreditsNeeded = CostCalculator.calculate(
    "search",
    numberOfEmailsToReveal,
  )
  let neededCredits = numberOfCreditsNeeded - numberOfCreditsLeft
  if (neededCredits < 0) {
    neededCredits = 0
  }
  const revealAllOperationCost = CostCalculator.calculateVisualRepresentation(
    "search",
    numberOfEmailsToReveal,
  )

  const [revealingGeneric, setRevealingGeneric] = React.useState(false)
  const genericRevealed = React.useMemo(() => {
    return revealingGeneric || genericResults.some((result) => result.revealed)
  }, [genericResults, revealingGeneric])

  return (
    <>
      <TooltipProvider>
        <div className="board-box ds-results">
          <header className="ds-results__header">
            <h2
              className="ds-results__title"
              data-domain-search--search-target="resultsTitle"
            >
              <strong>
                {i18n.t("js.search.results", { count: numberOfResults })}
              </strong>{" "}
              {i18n.t("js.search.for")} {domain.value}
            </h2>
            <form
              className="ds-finder-form ds-finder-form--makers"
              data-controller="domain-search--email-finder loading"
              data-domain-search--email-finder-domain-value={domain.value}
              data-loading-text-value=""
              data-domain-search--email-finder-version-value="domain_search"
              data-action="domain-search--email-finder#find loading#show domain-search--email-finder:loaded@window->loading#hide domain-search--email-finder:loaded@window->domain-search--search#updateResultsTitle"
            >
              <div className="ds-finder-form-name">
                <svg className="ds-finder-form-name__icon">
                  <use xlinkHref="#icon-email-finder" />
                </svg>
                <input
                  id="full-name-field"
                  className="ds-finder-form-name__field"
                  type="text"
                  placeholder={i18n.t(
                    "js.search.find_by_name.fullname_placeholder",
                  )}
                  data-domain-search--email-finder-target="name"
                  data-focus-element-target="element"
                />
              </div>
              <button
                className="ds-finder-form__submit"
                type="button"
                aria-label={i18n.t("js.search.find_by_name.find_this_person")}
                data-loading-target="element"
                data-action="domain-search--email-finder#find loading#show"
              >
                <span className="far fa-search"></span>
              </button>
            </form>
          </header>

          <Filters
            id="company-emails-filters"
            filters={filters}
            setFilters={(newFilters) => setFilters(newFilters)}
            jobTitles={jobTitles}
            anyFiltersSet={anyFiltersSet}
            clearFilters={() => clearFilters(setFilters)}
            possibleFilters={[
              "department",
              "job_title",
              "already_saved",
              "verification_status",
              "required_field",
              "location",
            ]}
          />

          <turbo-frame id="domain_search_email_finder_email" />

          {/* Bulk Actions Toolbar - matches original ds-bulk-actions structure */}
          <BulkActionsToolbar
            className={selectedIds.length > 0 ? "is-visible" : ""}
            selectedIds={selectedIds}
            numberOfResults={numberOfResults}
            currentCredits={numberOfCreditsLeft}
            currentTab={tabSelected}
            numberOfResultsForCurrentTab={
              tabSelected === "all_people"
                ? allPeopleResults.length
                : tabSelected === "decision_makers"
                  ? decisionMakersResults.length
                  : genericResults.length
            }
            removeSelection={() => setSelectedIds([])}
            selectAll={() =>
              setSelectedIds(filteredResults.map((result) => result.id))
            }
            selectCurrentTab={() => {
              const resultsToSelect =
                tabSelected === "all_people"
                  ? allPeopleResults
                  : tabSelected === "decision_makers"
                    ? decisionMakersResults
                    : genericResults

              setSelectedIds((prev) => [
                ...new Set([
                  ...prev,
                  ...resultsToSelect.map((result) => result.id),
                ]),
              ])
            }}
            saveLeads={saveLeads}
            handleLeadManagerSave={handleLeadManagerSave}
            leadsLists={leadsLists}
            reavealingIds={reavealingIds}
            revealAllOperationCost={revealAllOperationCost}
            exportEmails={exportEmails}
          />

          <EmailsList
            results={filteredResults}
            anyFiltersSet={anyFiltersSet}
            selectedIds={selectedIds}
            campaigns={campaigns}
            leadsLists={leadsLists}
            isLoading={isLoading}
            showGenericAlert={!genericRevealed}
            handleReveal={handleReveal}
            handleLeadsListSave={handleLeadsListSave}
            handleCampaignsSave={handleCampaignsSave}
            handleSelect={handleSelect}
            onTabSelected={setTabSelected}
            clearFilters={() => clearFilters(setFilters)}
          />

          {revealedIds.length > 0 && (
            <Snackbar className="m-4">
              <Icon
                icon="check-circle"
                variant="solid"
                size="sm"
                className="text-success"
              />
              <SnackbarText>
                <strong className="fw-medium">
                  {i18n.t("js.multi_domain_search.footer.leads", {
                    count: revealedIds.length,
                    pretty_count: CurrencyHelper.numberWithDelimiter(
                      revealedIds.length,
                    ),
                  })}
                </strong>
                &nbsp;
                {i18n.t("js.multi_domain_search.footer.saved")}
              </SnackbarText>
              <SnackbarLink
                href={leads_path({
                  updated_at: "last1h",
                })}
                turboFrame="_top"
              >
                {i18n.t("js.multi_domain_search.footer.see_in_leads")}
              </SnackbarLink>
            </Snackbar>
          )}
        </div>
      </TooltipProvider>
    </>
  )
}
